import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { ActionItem, RiskLite, SyncLog } from '@/lib/homeSummaries';

// Data for My Day (user), Program Board (program manager) and Workspace Health (admin).
// All of it is the caller's own tenant data under RLS. A table a role may not read comes back empty or errors;
// each hook reports that separately (`…Readable` / `…Failed`) so the screen can say "not visible to your role"
// instead of showing an empty state that reads as "nothing to do".

const displayName = async (userId: string): Promise<string | null> => {
  const { data, error } = await supabase.from('profiles').select('display_name').eq('user_id', userId).maybeSingle();
  if (error) throw new Error(error.message);
  return data?.display_name ?? null;
};

export function useMyDayData() {
  const { user } = useAuth();
  const actions = useQuery({
    queryKey: ['home', 'myday', 'actions'],
    staleTime: 60_000,
    queryFn: async (): Promise<ActionItem[]> => {
      const { data, error } = await supabase.from('action_items')
        .select('id,title,assignee_id,assignee_name,due_date,status').order('due_date', { ascending: true }).limit(200);
      if (error) throw new Error(error.message);
      return (data ?? []) as ActionItem[];
    },
  });
  const profile = useQuery({ queryKey: ['home', 'profile', user?.id], enabled: !!user?.id, staleTime: 300_000, queryFn: () => displayName(user!.id) });
  const meetings = useQuery({
    queryKey: ['home', 'myday', 'meetings'],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('meetings_sync').select('id,title,start_time').order('start_time', { ascending: false }).limit(4);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
  const request = useQuery({
    queryKey: ['home', 'myday', 'roleRequest', user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('role_requests')
        .select('id,requested_role,status,created_at').eq('user_id', user!.id).order('created_at', { ascending: false }).limit(1).maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });
  return {
    userId: user?.id ?? null,
    myName: profile.data ?? null,
    actions: actions.data ?? [], actionsFailed: actions.isError,
    meetings: meetings.data ?? [], meetingsFailed: meetings.isError,
    latestRequest: request.data ?? null,
    isLoading: actions.isLoading || meetings.isLoading,
  };
}

export function useProgramData() {
  const risks = useQuery({
    queryKey: ['home', 'program', 'risks'],
    staleTime: 60_000,
    queryFn: async (): Promise<RiskLite[]> => {
      const { data, error } = await supabase.from('risk_signals')
        .select('id,entity_type,entity_id,rule_id,severity,detected_at').is('resolved_at', null).order('detected_at', { ascending: false }).limit(200);
      if (error) throw new Error(error.message);
      return (data ?? []) as RiskLite[];
    },
  });
  const tasks = useQuery({
    queryKey: ['home', 'program', 'tasks'],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('tasks_sync').select('id,external_id,title,project_external_id,status').neq('status', 'done').limit(1000);
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
  const pending = useQuery({
    queryKey: ['home', 'program', 'pendingActions'],
    staleTime: 60_000,
    queryFn: async (): Promise<ActionItem[]> => {
      const { data, error } = await supabase.from('action_items')
        .select('id,title,assignee_id,assignee_name,due_date,status').eq('status', 'suggested').order('created_at', { ascending: true }).limit(20);
      if (error) throw new Error(error.message);
      return (data ?? []) as ActionItem[];
    },
  });
  return {
    risks: risks.data ?? [], risksFailed: risks.isError,
    tasks: tasks.data ?? [],
    pendingActions: pending.data ?? [], pendingFailed: pending.isError,
    isLoading: risks.isLoading || tasks.isLoading,
  };
}

export interface RoleRequestRow { id: string; requested_role: string; status: string; created_at: string; }

export function useWorkspaceData() {
  const { tenantId } = useAuth();
  const logs = useQuery({
    queryKey: ['home', 'workspace', 'syncLogs'],
    staleTime: 60_000,
    queryFn: async (): Promise<SyncLog[]> => {
      const since = new Date(Date.now() - 24 * 3_600_000).toISOString();
      const { data, error } = await supabase.from('integration_sync_logs')
        .select('id,integration_id,status,started_at,error_message').gte('started_at', since).order('started_at', { ascending: false }).limit(200);
      if (error) throw new Error(error.message);
      return (data ?? []) as SyncLog[];
    },
  });
  const members = useQuery({
    queryKey: ['home', 'workspace', 'members', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase.from('tenant_members').select('user_id', { count: 'exact', head: true }).eq('tenant_id', tenantId as string);
      if (error) throw new Error(error.message);
      return count ?? 0;
    },
  });
  const requests = useQuery({
    queryKey: ['home', 'workspace', 'roleRequests'],
    staleTime: 60_000,
    queryFn: async (): Promise<RoleRequestRow[]> => {
      const { data, error } = await supabase.from('role_requests')
        .select('id,requested_role,status,created_at').eq('status', 'pending').order('created_at', { ascending: true }).limit(20);
      if (error) throw new Error(error.message);
      return (data ?? []) as RoleRequestRow[];
    },
  });
  const killSwitch = useQuery({
    queryKey: ['home', 'workspace', 'killSwitch'],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('kill_switch_state').select('is_active,reason,activated_at').limit(1).maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });
  return {
    logs: logs.data ?? [], logsFailed: logs.isError,
    /** null = could not be read. */
    memberCount: members.isError ? null : members.data ?? null,
    requests: requests.data ?? [], requestsFailed: requests.isError,
    /** undefined = could not be read; null = no state row (never activated). */
    killSwitch: killSwitch.isError ? undefined : killSwitch.data ?? null,
    isLoading: logs.isLoading || requests.isLoading,
  };
}
